import moment from 'moment';
import { CalendarReportData, CalendarDay, ShiftDetail, SlotDetail } from '../redux/CalendarReportReducer/calendarReportSlice';
import { GigRequestDetail } from '../types/gigApi';

interface ShiftEvent {
    id: string;
    time: string;
    startTime?: string;
    endTime?: string;
    shiftType: string;
    status: 'APPROVED' | 'POSTED' | 'PENDING APPROVAL' | 'REJECTED' | 'CANCELLED';
    date: Date;
    claimedBy?: string;
    claimedByName?: string;
    claimedByUserNm?: string;
    postedByUserId?: string;
    postedByUserNm?: string;
    team?: string;
    skill?: string;
    skillCd?: string;
    skillDesc?: string;
    requestId?: number;
    requestSeqId?: number;
    requestHours?: number;
    requestNotes?: string;
    rejectionNotes?: string;
}

/**
 * Normalize shift type abbreviations by stripping the "-D" (direct-report) suffix.
 * e.g. "SFT-D" -> "SFT", "VTO-D" -> "VTO"
 */
const normalizeShiftType = (shiftType: string): string => {
    if (!shiftType) return shiftType;
    return shiftType.replace(/-D$/i, '');
};

/**
 * Map API status codes to display status
 */
const mapStatus = (apiStatus: string): ShiftEvent['status'] => {
    const statusMap: Record<string, ShiftEvent['status']> = {
        'PST': 'POSTED',
        'APP': 'APPROVED',
        'PND': 'PENDING APPROVAL',
        'DEN': 'REJECTED',
        'CAN': 'CANCELLED',
    };
    return statusMap[apiStatus] || 'POSTED';
};

/**
 * Format time from HH:mm to h:mm AM/PM
 */
const formatTime = (time24: string): string => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Format a start/end time pair as a display range, e.g. "8:00 AM - 4:00 PM".
 * Falls back to just the start time if the end time is missing.
 */
const formatTimeRange = (startTime24?: string, endTime24?: string): string => {
    const start = formatTime(startTime24 || '');
    const end = formatTime(endTime24 || '');
    if (!start) return '';
    if (!end) return start;
    return `${start} - ${end}`;
};

/**
 * Group slots by their status
 */
const groupSlotsByStatus = (slots: SlotDetail[]): Record<string, SlotDetail[]> => {
    const statusGroups: Record<string, SlotDetail[]> = {};

    slots.forEach((slot) => {
        const status = mapStatus(slot.status);
        if (!statusGroups[status]) {
            statusGroups[status] = [];
        }
        statusGroups[status].push(slot);
    });

    return statusGroups;
};

/**
 * Create shift events from a slot with shift details
 */
const createShiftEvent = (
    slot: SlotDetail,
    shift: ShiftDetail,
    status: string,
    date: Date
): ShiftEvent => ({
    id: `${shift.requestId}-${slot.seqId}`,
    time: formatTimeRange(shift.startTime, shift.endTime),
    startTime: shift.startTime,
    endTime: shift.endTime,
    shiftType: normalizeShiftType(shift.requestType),
    status: status as ShiftEvent['status'],
    date: date,
    claimedBy: slot.claimedByUserId || undefined,
    claimedByName: slot.claimedByUserNm || undefined,
    claimedByUserNm: slot.claimedByUserNm || undefined,
    postedByUserId: shift.postedByUserId || undefined,
    postedByUserNm: shift.postedByUserNm || undefined,
    team: shift.requiredTeamCd ? shift.requiredTeamCd.replaceAll(',', ', ') : undefined,
    skill: shift.requiredSkillDesc,
    skillCd: shift.requiredSkillCd,
    skillDesc: shift.requiredSkillDesc,
});

/**
 * Process a single shift and return its events
 */
const processShift = (shift: ShiftDetail, date: Date): ShiftEvent[] => {
    const events: ShiftEvent[] = [];
    const statusGroups = groupSlotsByStatus(shift.slotDetails);

    Object.entries(statusGroups).forEach(([status, slots]) => {
        slots.forEach((slot) => {
            events.push(createShiftEvent(slot, shift, status, date));
        });
    });

    return events;
};

/**
 * Process a single calendar day and return its events
 */
const processCalendarDay = (calendarDay: CalendarDay): ShiftEvent[] => {
    // Parse as local calendar date (not UTC) to avoid the date shifting
    // backward/forward a day depending on the viewer's timezone offset.
    const date = moment(calendarDay.date, 'YYYY-MM-DD').toDate();
    const events: ShiftEvent[] = [];

    calendarDay.shifts.forEach((shift) => {
        events.push(...processShift(shift, date));
    });

    return events;
};

/**
 * Transform API CalendarReportData into ShiftEvent[] format for calendar display
 * Groups shifts by time, type, team, skill, and status at the transformation level
 */
export const transformCalendarData = (apiData: CalendarReportData): ShiftEvent[] => {
    const events: ShiftEvent[] = [];

    apiData.calendar.forEach((calendarDay) => {
        events.push(...processCalendarDay(calendarDay));
    });

    return events;
};

const getGigCalendarStatus = (pendingSlots?: number, openSlots?: number): ShiftEvent['status'] => {
    if ((pendingSlots ?? 0) > 0) return 'PENDING APPROVAL';
    if ((openSlots ?? 0) > 0) return 'POSTED';
    return 'APPROVED';
};

/**
 * Transform gig calendar API response (AvailableShiftRequest[]) into ShiftEvent[]
 * so gig shifts can be rendered alongside regular shifts on the calendar.
 */
export const transformGigCalendarData = (
    gigShifts: Array<{
        siteCd?: string;
        requestId?: number;
        requestSeqId?: number;
        requestDt?: string;
        startTime?: string;
        endTime?: string;
        shiftType?: string;
        requiredSkillCd?: string;
        requiredSkillDesc?: string;
        requiredTeamCd?: string;
        requiredEmployeeCount: number;
        openSlots: number;
        pendingSlots: number;
    }>
): ShiftEvent[] => {
    const events: ShiftEvent[] = [];

    gigShifts.forEach((g) => {
        if (!g.requestDt) return;
        const date = moment(g.requestDt, 'YYYY-MM-DD').toDate();
        const status = getGigCalendarStatus(g.pendingSlots, g.openSlots);

        events.push({
            id: `gig-${g.requestId ?? ''}-${g.requestSeqId ?? ''}`,
            time: formatTimeRange(g.startTime, g.endTime),
            startTime: g.startTime,
            endTime: g.endTime,
            shiftType: 'GIG',
            status,
            date,
            team: g.requiredTeamCd ? g.requiredTeamCd.replaceAll(',', ', ') : undefined,
            skill: g.requiredSkillDesc,
            skillCd: g.requiredSkillCd,
            skillDesc: g.requiredSkillDesc,
        });
    });

    return events;
};

/**
 * Transform GET /api/gig/requests details into ShiftEvent[] for the calendar.
 * One `GigRequestDetail` row = one calendar event.
 */
export const transformGigRequestDetails = (rows: GigRequestDetail[] | undefined | null): ShiftEvent[] => {
    if (!rows || rows.length === 0) return [];
    return rows.map((row) => {
        const date = moment(row.requestDt, 'YYYY-MM-DD').toDate();
        return {
            id: `gig-${row.requestId}-${row.requestSeqId}`,
            time: row.requestHours != null ? `${row.requestHours} hrs` : '',
            startTime: row.startTime ?? undefined,
            endTime: row.endTime ?? undefined,
            shiftType: 'GIG',
            status: mapStatus(row.requestStatus ?? 'PST'),
            date,
            postedByUserId: row.requestedByUserId ?? undefined,
            postedByUserNm: row.requestedByUserNm ?? undefined,
            team: row.requestedShiftCd ?? undefined,
            skill: row.requestedSkillDesc ?? row.requestedSkillCd ?? undefined,
            skillCd: row.requestedSkillCd ?? undefined,
            skillDesc: row.requestedSkillDesc ?? row.requestedSkillCd ?? undefined,
            requestId: row.requestId,
            requestSeqId: row.requestSeqId,
            requestHours: row.requestHours ?? undefined,
            requestNotes: row.requestDetailNotes ?? row.requestNotes ?? undefined,
            rejectionNotes: row.rejectionNotes ?? undefined,
        };
    });
};